import {
  Button,
  CheckIcon,
  CloseButton,
  Group,
  Select,
  Text,
  TextInput
} from '@mantine/core'
import { IconSearch } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { DeviceLogLevel } from '@/types/device'
import { DeviceLogLevelBadge } from './DeviceLogLevelBadge'
import { GROUP_MESSAGES_ONLY } from './buildGroupLogParams'
import {
  AppLogFilterState,
  DEFAULT_FILTERS,
  DateRangeOption
} from './deploymentAppLogsFilterConfig'

const LOG_LEVELS: DeviceLogLevel[] = [
  'info',
  'warning',
  'error',
  'critical',
  'debug'
]
const DATE_RANGE_OPTIONS: DateRangeOption[] = ['15m', '1h', '6h', '24h']

type DeploymentAppLogsFiltersProps = {
  filters: AppLogFilterState
  cameraOptions?: { value: string; label: string }[]
  onChange: (filters: AppLogFilterState) => void
}

export const DeploymentAppLogsFilters = ({
  filters,
  cameraOptions,
  onChange
}: DeploymentAppLogsFiltersProps) => {
  const { formatMessage } = useIntl()

  const levelOptions = [
    {
      value: 'all',
      label: formatMessage({
        id: 'deployments.details.tabs.appLogs.filters.level.all'
      })
    },
    ...LOG_LEVELS.map((level) => ({ value: level, label: level }))
  ]

  const dateRangeOptions = [
    {
      value: 'all',
      label: formatMessage({
        id: 'deployments.details.tabs.appLogs.filters.dateRange.all'
      })
    },
    ...DATE_RANGE_OPTIONS.map((value) => ({
      value,
      label: formatMessage({
        id: `deployments.details.tabs.appLogs.filters.dateRange.${value}`
      })
    }))
  ]

  const cameraSelectOptions = cameraOptions && [
    {
      value: 'all',
      label: formatMessage({
        id: 'deployments.details.tabs.appLogs.filters.camera.all'
      })
    },
    {
      value: GROUP_MESSAGES_ONLY,
      label: formatMessage({
        id: 'deployments.details.tabs.appLogs.filters.camera.groupOnly'
      })
    },
    ...cameraOptions
  ]

  const isFiltered =
    filters.search !== undefined ||
    filters.level !== undefined ||
    filters.dateRange !== undefined ||
    filters.camera !== undefined

  return (
    <Group gap="xs">
      <TextInput
        size="xs"
        w={260}
        leftSection={<IconSearch size={14} />}
        placeholder={formatMessage({
          id: 'deployments.details.tabs.appLogs.filters.search.placeholder'
        })}
        value={filters.search ?? ''}
        rightSection={
          filters.search && (
            <CloseButton
              size="xs"
              onClick={() => onChange({ ...filters, search: undefined })}
            />
          )
        }
        onChange={(e) =>
          onChange({ ...filters, search: e.currentTarget.value || undefined })
        }
      />

      {cameraSelectOptions && (
        <Select
          size="xs"
          w={180}
          allowDeselect={false}
          aria-label={formatMessage({
            id: 'deployments.details.tabs.appLogs.columns.camera'
          })}
          data={cameraSelectOptions}
          value={filters.camera ?? 'all'}
          onChange={(value) =>
            onChange({
              ...filters,
              camera: value && value !== 'all' ? value : undefined
            })
          }
        />
      )}

      <Select
        size="xs"
        w={130}
        data={levelOptions}
        value={filters.level ?? 'all'}
        allowDeselect={false}
        renderOption={({ option, checked }) => (
          <Group gap="xs">
            {checked && (
              <CheckIcon size={10} color="var(--mantine-color-dimmed)" />
            )}
            {option.value !== 'all' ? (
              <DeviceLogLevelBadge level={option.value as DeviceLogLevel} />
            ) : (
              <Text size="xs">{option.label}</Text>
            )}
          </Group>
        )}
        onChange={(value) =>
          onChange({
            ...filters,
            level: value !== 'all' ? (value as DeviceLogLevel) : undefined
          })
        }
      />

      <Select
        size="xs"
        w={130}
        allowDeselect={false}
        data={dateRangeOptions}
        value={filters.dateRange ?? 'all'}
        onChange={(value) =>
          onChange({
            ...filters,
            dateRange: value !== 'all' ? (value as DateRangeOption) : undefined
          })
        }
      />

      {isFiltered && (
        <Button
          size="xs"
          variant="subtle"
          onClick={() => onChange(DEFAULT_FILTERS)}
        >
          <FormattedMessage id="general.clearFilters" />
        </Button>
      )}
    </Group>
  )
}
